import React, { useState, useEffect, useRef } from 'react';
import { ChevronLeft, ChevronRight, Play, MonitorSmartphone } from 'lucide-react';

interface Video {
  id: number;
  url: string;
  aspectRatio: string;
  thumbnail?: string;
}

interface VideoCarouselProps {
  videos: Video[];
}

export const VideoCarousel: React.FC<VideoCarouselProps> = ({ videos }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showPlaylist, setShowPlaylist] = useState(false);
  const iframeRefs = useRef<(HTMLIFrameElement | null)[]>([]);

  // Function to stop the current video
  const stopCurrentVideo = () => {
    const currentIframe = iframeRefs.current[currentIndex];
    if (currentIframe?.contentWindow) {
      currentIframe.contentWindow.postMessage(
        JSON.stringify({ method: 'pause' }),
        '*'
      );
    }
  };

  const handlePrevious = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      stopCurrentVideo();
      setCurrentIndex((prev) => (prev === 0 ? videos.length - 1 : prev - 1));
    }
  };

  const handleNext = () => {
    if (!isAnimating) {
      setIsAnimating(true);
      stopCurrentVideo();
      setCurrentIndex((prev) => (prev === videos.length - 1 ? 0 : prev + 1));
    }
  };

  const handlePlaylistClick = (index: number) => {
    if (index !== currentIndex) {
      stopCurrentVideo();
      setCurrentIndex(index);
      setShowPlaylist(false);
    }
  };

  const handleTransitionEnd = () => {
    setIsAnimating(false);
  };

  // Set up iframe refs array when videos change
  useEffect(() => {
    iframeRefs.current = iframeRefs.current.slice(0, videos.length);
  }, [videos]);

  return (
    <div className="flex flex-col lg:flex-row gap-4">
      {/* Main Video Player */}
      <div className="relative w-full lg:w-3/4 overflow-hidden bg-gray-900 rounded-xl">
        <div
          className="flex transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          onTransitionEnd={handleTransitionEnd}
        >
          {videos.map((video, index) => (
            <div
              key={video.id}
              className="w-full flex-shrink-0 relative"
              style={{ paddingBottom: video.aspectRatio }}
            >
              <iframe
                ref={(el) => (iframeRefs.current[index] = el)}
                src={`${video.url}&autoplay=0`}
                className="absolute inset-0 w-full h-full"
                frameBorder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
                title={`Cercle Poincaré Video ${index + 1}`}
              />
            </div>
          ))}
        </div>

        <button
          onClick={handlePrevious}
          className="absolute left-2 lg:left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 p-1.5 lg:p-2 rounded-full backdrop-blur-sm transition-colors z-10"
          disabled={isAnimating}
        >
          <ChevronLeft className="h-4 w-4 lg:h-6 lg:w-6 text-white" />
        </button>

        <button
          onClick={handleNext}
          className="absolute right-2 lg:right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 p-1.5 lg:p-2 rounded-full backdrop-blur-sm transition-colors z-10"
          disabled={isAnimating}
        >
          <ChevronRight className="h-4 w-4 lg:h-6 lg:w-6 text-white" />
        </button>

        {/* Mobile Playlist Toggle */}
        <button
          onClick={() => setShowPlaylist(!showPlaylist)}
          className="lg:hidden absolute bottom-4 right-4 bg-white/20 hover:bg-white/30 p-2 rounded-full backdrop-blur-sm transition-colors z-10"
        >
          <MonitorSmartphone className="h-5 w-5 text-white" />
        </button>
      </div>

      {/* Playlist Sidebar - Desktop */}
      <div className="hidden lg:block w-1/4 bg-white rounded-xl shadow-lg overflow-hidden">
        <div className="p-4 bg-gray-50 border-b">
          <h3 className="font-semibold text-gray-900">Playlist</h3>
        </div>
        <div className="overflow-y-auto max-h-[400px] scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
          {videos.map((video, index) => (
            <button
              key={video.id}
              onClick={() => handlePlaylistClick(index)}
              className={`w-full p-4 flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                index === currentIndex ? 'bg-primary-50' : ''
              }`}
            >
              <div className="relative w-12 h-12 flex-shrink-0 bg-gray-100 rounded-lg flex items-center justify-center">
                <Play className={`h-5 w-5 ${index === currentIndex ? 'text-primary-600' : 'text-gray-400'}`} />
              </div>
              <span className={`text-sm ${index === currentIndex ? 'text-primary-600 font-medium' : 'text-gray-600'}`}>
                Vidéo {index + 1}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Mobile Playlist Modal */}
      {showPlaylist && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-50 flex items-end">
          <div className="bg-white w-full rounded-t-xl max-h-[70vh] overflow-hidden">
            <div className="p-4 bg-gray-50 border-b flex justify-between items-center">
              <h3 className="font-semibold text-gray-900">Playlist</h3>
              <button
                onClick={() => setShowPlaylist(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
            <div className="overflow-y-auto max-h-[calc(70vh-4rem)]">
              {videos.map((video, index) => (
                <button
                  key={video.id}
                  onClick={() => handlePlaylistClick(index)}
                  className={`w-full p-4 flex items-center gap-3 hover:bg-gray-50 transition-colors ${
                    index === currentIndex ? 'bg-primary-50' : ''
                  }`}
                >
                  <div className="relative w-12 h-12 flex-shrink-0 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Play className={`h-5 w-5 ${index === currentIndex ? 'text-primary-600' : 'text-gray-400'}`} />
                  </div>
                  <span className={`text-sm ${index === currentIndex ? 'text-primary-600 font-medium' : 'text-gray-600'}`}>
                    Vidéo {index + 1}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};